import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// Admin phone numbers
const ADMIN_PHONE_NUMBERS = [
  '+15407939110',
];

function Home() {
  const [hasResponse, setHasResponse] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const { currentUser, signOut } = useAuth();
  const navigate = useNavigate();
  
  const isAdmin = currentUser && ADMIN_PHONE_NUMBERS.includes(currentUser.phoneNumber);

  // Helper function to check if user has already submitted a response
  const checkExistingResponse = useCallback(async (userId) => {
    if (!userId) {
      return false;
    }

    try {
      // Check Firestore first
      if (db) {
        const responsesRef = collection(db, 'responses');
        const q = query(responsesRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          return true;
        }
      }
    } catch (error) {
      console.warn('Error checking Firestore, checking localStorage:', error);
    }
    
    // Fallback: check localStorage
    try {
      const localResponses = JSON.parse(localStorage.getItem('localResponses') || '[]');
      const existingResponse = localResponses.find(response => response.userId === userId);
      return !!existingResponse;
    } catch (error) {
      console.warn('Error checking localStorage:', error);
      return false;
    }
  }, []);

  // Helper function to check if user is confirmed
  const checkConfirmedStatus = useCallback(async (userId) => {
    if (!userId) {
      return false;
    }

    try {
      // Check Firestore first
      if (db) {
        const responsesRef = collection(db, 'responses');
        const q = query(responsesRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const userResponse = querySnapshot.docs[0].data();
          return userResponse.status === 'confirmed';
        }
      }
    } catch (error) {
      console.warn('Error checking Firestore, checking localStorage:', error);
    }
    
    // Fallback: check localStorage
    try {
      const localResponses = JSON.parse(localStorage.getItem('localResponses') || '[]');
      const userResponse = localResponses.find(response => response.userId === userId);
      return userResponse?.status === 'confirmed';
    } catch (error) {
      console.warn('Error checking localStorage:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    const isDevMode = localStorage.getItem('devMode') === 'true';
    if (!currentUser && !isDevMode) {
      navigate('/');
      return;
    }

    const checkResponse = async () => {
      if (currentUser && currentUser.uid) {
        const hasResponseResult = await checkExistingResponse(currentUser.uid);
        setHasResponse(hasResponseResult);
        
        // Check confirmation status (admins are always confirmed)
        if (isAdmin) {
          setIsConfirmed(true);
        } else {
          const confirmedResult = await checkConfirmedStatus(currentUser.uid);
          setIsConfirmed(confirmedResult);
        }
      } else if (isDevMode) {
        // For dev mode, check with dev-user ID
        const hasResponseResult = await checkExistingResponse('dev-user');
        setHasResponse(hasResponseResult);
        // In dev mode, allow access (set as confirmed)
        setIsConfirmed(true);
      }
      setLoading(false);
    };

    checkResponse();
  }, [currentUser, navigate, checkExistingResponse, checkConfirmedStatus, isAdmin]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  const userName = localStorage.getItem('userName') || 'User';

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <h1 className="card-title">🏔️ Welcome, {userName}!</h1>
          <p className="card-subtitle">
            Manage your Whistler trip details and preferences
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {/* Trip Details Button - Only show if confirmed */}
          {(isConfirmed || isAdmin) && (
            <button
              onClick={() => navigate('/trip-details')}
              className="btn btn-primary"
            >
              📅 Trip Details
            </button>
          )}

          {/* Carpools Button - Only show if confirmed */}
          {(isConfirmed || isAdmin) && (
            <button
              onClick={() => navigate('/carpools', { state: { from: '/home' } })}
              className="btn btn-primary"
            >
              🚗 Carpools
            </button>
          )}

          {/* Profile Button */}
          <button
            onClick={() => navigate('/profile')}
            className="btn btn-primary"
          >
            👤 Profile
          </button>

          {/* Social Button - Only show if confirmed */}
          {(isConfirmed || isAdmin) && (
            <button
              onClick={() => navigate('/social')}
              className="btn btn-primary"
            >
              👥 Social
            </button>
          )}

          {/* Survey or View/Modify Responses Button */}
          {hasResponse ? (
            <button
              onClick={() => navigate('/edit-responses')}
              className="btn btn-primary"
            >
              📝 View/Modify Responses
            </button>
          ) : (
            <button
              onClick={() => navigate('/survey')}
              className="btn btn-primary"
            >
              ✏️ Take Survey
            </button>
          )}

          {/* Admin Portal Button - Only visible to admins */}
          {isAdmin && (
            <button
              onClick={() => navigate('/admin')}
              className="btn btn-primary"
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)',
                color: 'white',
                border: 'none'
              }}
            >
              📊 Admin Portal
            </button>
          )}

          {/* Log Out Button */}
          <button
            onClick={handleLogout}
            className="btn btn-secondary"
            style={{
              marginTop: '10px',
              background: '#fed7d7',
              color: '#c53030',
              border: '2px solid #feb2b2'
            }}
          >
            🚪 Log Out
          </button>
        </div>
      </div>
    </div>
  );
}

export default Home;
